import toast from 'react-hot-toast';
import { customFetch, getAuthHeaders, getAuthOnlyHeaders } from './apiService';

export const AI_MEDIA_FOLDER = 'class/docs';
export const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
export const MAX_DOC_SIZE_BYTES = 15 * 1024 * 1024; // 15MB
export const MAX_DOC_COUNT = 5; // Tối đa 5 file/lần

export const AI_MEDIA_RETENTION_NOTICE =
  '⏳ Để bảo mật dữ liệu và tối ưu hệ thống, file phục vụ AI của bạn sẽ tự động bị xóa sau 15 ngày.';

export interface MediaLibraryItem {
  id: string;
  name: string;
  fileName: string;
  url: string;
  fileUrl: string;
  size: number;
  sizeFormatted?: string;
  type: 'image' | 'doc';
  createdAt: string;
}

/**
 * Format kích thước byte thành KB hoặc MB dễ đọc
 */
export const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes <= 0) return '0 KB';
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Kiểm tra xem lỗi có phải do file URL quá hạn lưu trữ (> 15 ngày) hay không
 */
export const isExpiredFileError = (error: any): boolean => {
  if (!error) return false;
  const msg = (error.message || error.toString() || '').toLowerCase();
  const code = (error.code || '').toLowerCase();
  return (
    code.includes('expired') ||
    code.includes('file_expired') ||
    msg.includes('expired') ||
    msg.includes('hết hạn') ||
    msg.includes('quá 15 ngày') ||
    msg.includes('tự động bị xóa')
  );
};

/**
 * 2.1 Tải lên Hình ảnh cho AI (POST /api/v1/media/upload)
 * - Giới hạn Frontend: Tối đa 10MB / 1 file
 * - Bắt buộc folder: 'class/docs'
 * - Key FormData: 'files'
 */
export const uploadImageFilesForAi = async (files: File[]): Promise<string[]> => {
  if (!files || files.length === 0) return [];

  // PRE-CHECK 1: Dung lượng tối đa 10MB/file
  for (const file of files) {
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      const errorMsg = `Ảnh "${file.name}" quá lớn (${formatFileSize(file.size)}). Giới hạn tối đa là 10MB / 1 file ảnh.`;
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  // Khởi tạo FormData theo đúng yêu cầu:
  // - Request Upload File gọi lên API /api/v1/media/upload
  // - Bắt buộc phải append thêm tham số folder vào FormData: folder='class/docs'
  // - Gắn các file ảnh vào FormData
  const formData = new FormData();
  formData.append('folder', AI_MEDIA_FOLDER);
  if (files.length === 1) {
    formData.append('file', files[0]);
  }
  files.forEach((file) => {
    formData.append('files', file);
  });

  // Gọi API /api/v1/media/upload
  // LƯU Ý SỬA LỖI:
  // KHÔNG ĐƯỢC truyền header 'Content-Type': 'application/json' khi gửi FormData!
  // Nếu truyền 'application/json', body-parser.json() trên máy chủ backend sẽ cố parse multipart boundary
  // (bắt đầu bằng dấu '-') và ném lỗi: "SyntaxError: Unexpected token - in JSON at position 0".
  // Dùng getAuthOnlyHeaders() để browser tự thiết lập header Content-Type multipart/form-data kèm boundary.
  const uploadUrl = `/api/v1/media/upload?folder=${encodeURIComponent(AI_MEDIA_FOLDER)}`;
  const res = await customFetch(uploadUrl, {
    method: 'POST',
    headers: getAuthOnlyHeaders(),
    credentials: 'include',
    body: formData,
  });

  const rawText = await res.text();
  let data: any;
  try {
    data = JSON.parse(rawText);
  } catch (parseErr) {
    const cleanMsg = rawText.includes('<pre>') 
      ? rawText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
      : (rawText.slice(0, 200) || 'Lỗi phản hồi từ máy chủ Media.');
    throw new Error(cleanMsg);
  }

  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Lỗi khi tải ảnh lên máy chủ Media.');
  }

  // Chuẩn hóa mảng fileUrls
  const returnedUrls: string[] = Array.isArray(data.fileUrls) && data.fileUrls.length > 0
    ? data.fileUrls
    : (Array.isArray(data.urls) ? data.urls : (data.fileUrl ? [data.fileUrl] : (data.url ? [data.url] : [])));

  return returnedUrls;
};

/**
 * 2.1 Tải lên Tài liệu văn bản cho AI (POST /api/v1/media/upload_docs)
 * - Giới hạn Frontend: Tối đa 15MB / 1 file. Tối đa 5 file/lần.
 * - Bắt buộc folder: 'class/docs'
 * - Key FormData: 'files'
 */
export const uploadDocFilesForAi = async (files: File[]): Promise<string[]> => {
  if (!files || files.length === 0) return [];

  // PRE-CHECK 1: Tối đa 5 file/lần
  if (files.length > MAX_DOC_COUNT) {
    const errorMsg = `Bạn chỉ có thể tải lên tối đa ${MAX_DOC_COUNT} tài liệu mỗi lần.`;
    toast.error(errorMsg);
    throw new Error(errorMsg);
  }

  // PRE-CHECK 2: Dung lượng tối đa 15MB/file
  for (const file of files) {
    if (file.size > MAX_DOC_SIZE_BYTES) {
      const errorMsg = `Tài liệu "${file.name}" quá lớn (${formatFileSize(file.size)}). Giới hạn tối đa là 15MB / 1 file.`;
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }
  }

  // Khởi tạo FormData theo đúng yêu cầu:
  // - Bắt buộc phải append thêm tham số folder vào FormData: folder='class/docs'
  // - Gắn các file tài liệu vào FormData
  const formData = new FormData();
  formData.append('folder', AI_MEDIA_FOLDER);
  if (files.length === 1) {
    formData.append('file', files[0]);
  }
  files.forEach((file) => {
    formData.append('files', file);
  });

  const uploadDocsUrl = `/api/v1/media/upload_docs?folder=${encodeURIComponent(AI_MEDIA_FOLDER)}`;
  const res = await customFetch(uploadDocsUrl, {
    method: 'POST',
    headers: getAuthOnlyHeaders(),
    credentials: 'include',
    body: formData,
  });

  const rawText = await res.text();
  let data: any;
  try {
    data = JSON.parse(rawText);
  } catch (parseErr) {
    const cleanMsg = rawText.includes('<pre>') 
      ? rawText.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
      : (rawText.slice(0, 200) || 'Lỗi phản hồi từ máy chủ Media.');
    throw new Error(cleanMsg);
  }

  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Lỗi khi tải tài liệu lên máy chủ Media.');
  }

  // Chuẩn hóa mảng fileUrls
  const returnedUrls: string[] = Array.isArray(data.fileUrls) && data.fileUrls.length > 0
    ? data.fileUrls
    : (Array.isArray(data.urls) ? data.urls : (data.fileUrl ? [data.fileUrl] : (data.url ? [data.url] : [])));

  return returnedUrls;
};

/**
 * 2.2 Tái sử dụng từ Thư viện (Library)
 * - Tab Hình ảnh: GET /api/v1/media/list?folder=class/docs&type=image
 * - Tab Tài liệu: GET /api/v1/media/list?folder=class/docs&type=doc
 */
export const getMediaListForAi = async (
  type: 'image' | 'doc'
): Promise<MediaLibraryItem[]> => {
  const url = `/api/v1/media/list?folder=${encodeURIComponent(AI_MEDIA_FOLDER)}&type=${encodeURIComponent(type)}`;
  const res = await customFetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  const data = await res.json();
  if (!res.ok || !data.success) {
    throw new Error(data.message || 'Không thể tải danh sách tài nguyên từ thư viện.');
  }

  const rawItems = Array.isArray(data.items)
    ? data.items
    : (Array.isArray(data.files) ? data.files : (Array.isArray(data.data) ? data.data : []));

  return rawItems.map((item: any, idx: number) => {
    const fileUrl = item.fileUrl || item.url || '';
    const fileName = item.fileName || item.name || (type === 'image' ? `image_${idx + 1}.png` : `document_${idx + 1}.pdf`);
    const size = Number(item.size) || 0;
    return {
      id: item.id || item._id || `${fileUrl}_${idx}`,
      name: fileName,
      fileName: fileName,
      url: fileUrl,
      fileUrl: fileUrl,
      size,
      sizeFormatted: item.sizeFormatted || formatFileSize(size),
      type,
      createdAt: item.createdAt || new Date().toISOString(),
    };
  });
};
